import React from "react";
import { Link } from "react-router-dom";
import { PageShell } from "@/components/layout/PageShell";
import { Seo } from "@/components/seo/Seo";
import { BreadcrumbSchema } from "@/components/seo/StructuredData";
import {
  STEWARD_ENTITY,
  STEWARDSHIP_BEGAN_ISO,
  FOUNDATION_EMAIL,
} from "@shared/campaign";

const FIRST_REPORT_DATE = "31 August 2026";

interface LedgerRow {
  date: string;
  item: string;
  amount_usd: number;
  direction: "in" | "out";
  source_or_recipient: string;
  notes: string;
}

const LEDGER_ROWS: readonly LedgerRow[] = [
  // First entries land at the close of the first reporting period
  // (FIRST_REPORT_DATE). The page renders an explicit empty state below
  // until then.
] as const;

export function Ledger() {
  return (
    <PageShell
      title="Public ledger"
      subtitle="Every wire, every expenditure, during the stewardship period."
      stewardship="Transparency · stewardship period"
    >
      <Seo
        title="Public ledger"
        description="The .person Foundation publishes every wire received and every expenditure made during the stewardship period, when funds are held by Mind Hack, Inc. as temporary fiduciary steward."
        path="/foundation/ledger"
      />
      <BreadcrumbSchema
        items={[
          { name: "Home", path: "/" },
          { name: "Foundation", path: "/foundation" },
          { name: "Ledger", path: "/foundation/ledger" },
        ]}
      />

      <section className="space-y-5 text-ink-2 font-serif text-base leading-relaxed mb-12">
        <p>
          The Foundation is not yet a US 501(c)(3); during the period before
          IRS determination, all monetary support is held by{" "}
          <strong className="text-ink">{STEWARD_ENTITY}</strong> as temporary
          fiduciary steward. Trust in that arrangement is only earned by
          publishing what comes in and what goes out &mdash; without
          aggregation, without reframing, without delay.
        </p>
        <p>
          This page is the public ledger. The first report covers the period
          beginning <strong className="text-ink">{STEWARDSHIP_BEGAN_ISO}</strong>{" "}
          and is due to be published on{" "}
          <strong className="text-ink">{FIRST_REPORT_DATE}</strong>. Subsequent
          reports are quarterly, on the last day of the calendar quarter.
        </p>
        <p>
          Bookkeeping is one of the items on the{" "}
          <Link
            to="/foundation/contribute#wishlist"
            className="text-ink underline decoration-hairline hover:decoration-ink"
          >
            Wish List
          </Link>{" "}
          &mdash; until that item is funded the ledger is maintained by the
          Founding Director directly, with the implicit limits that come with
          that.
        </p>
      </section>

      <section
        aria-labelledby="ledger-table-heading"
        className="border-t border-hairline pt-10 mb-14"
      >
        <h2
          id="ledger-table-heading"
          className="text-2xl font-serif text-ink mb-4"
        >
          Entries
        </h2>
        {LEDGER_ROWS.length === 0 ? (
          <div className="border border-hairline bg-paper-2 p-8 text-center">
            <p className="font-serif text-ink-2 mb-2">
              The first ledger entries will appear here on{" "}
              <strong className="text-ink">{FIRST_REPORT_DATE}</strong>.
            </p>
            <p className="font-mono text-[10px] uppercase tracking-widest text-ink-3">
              Stewardship period began {STEWARDSHIP_BEGAN_ISO}
            </p>
          </div>
        ) : (
          <div className="border border-hairline overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-paper-2 text-[10px] font-mono uppercase tracking-widest text-ink-3">
                <tr className="border-b border-hairline">
                  <th scope="col" className="text-left px-5 py-3 font-bold">
                    Date
                  </th>
                  <th scope="col" className="text-left px-5 py-3 font-bold">
                    Item
                  </th>
                  <th scope="col" className="text-right px-5 py-3 font-bold">
                    Amount (USD)
                  </th>
                  <th scope="col" className="text-left px-5 py-3 font-bold">
                    Direction
                  </th>
                  <th scope="col" className="text-left px-5 py-3 font-bold">
                    Source / recipient
                  </th>
                  <th scope="col" className="text-left px-5 py-3 font-bold">
                    Notes
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-hairline text-ink-2">
                {LEDGER_ROWS.map((row, i) => (
                  <tr key={`${row.date}-${i}`}>
                    <td className="px-5 py-3 font-mono text-xs">{row.date}</td>
                    <td className="px-5 py-3">{row.item}</td>
                    <td className="px-5 py-3 font-mono text-xs text-right">
                      {row.direction === "in" ? "+" : "−"}
                      {row.amount_usd.toLocaleString("en-US")}
                    </td>
                    <td className="px-5 py-3 font-mono text-[10px] uppercase tracking-widest">
                      {row.direction === "in" ? "Received" : "Paid"}
                    </td>
                    <td className="px-5 py-3">{row.source_or_recipient}</td>
                    <td className="px-5 py-3 text-xs">{row.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="border-t border-hairline pt-10">
        <h2 className="text-2xl font-serif text-ink mb-4">
          What gets published
        </h2>
        <ul className="list-disc list-inside text-ink-2 font-serif space-y-2 mb-6">
          <li>Every wire received, with date and approximate amount.</li>
          <li>
            Every expenditure made, with vendor, amount, and the Wish List or
            campaign line it was directed against.
          </li>
          <li>
            Personal donor identifiers withheld by default; named recognition
            only on opt-in.
          </li>
          <li>
            Reconciliation of stewardship-period activity at the close of the
            period, when funds and obligations transfer to the Foundation.
          </li>
        </ul>
        <p className="text-sm font-mono text-ink-3">
          Questions or corrections:{" "}
          <a
            href={`mailto:${FOUNDATION_EMAIL}?subject=Ledger%20question`}
            className="text-ink underline decoration-hairline hover:decoration-ink"
          >
            {FOUNDATION_EMAIL}
          </a>
          .
        </p>
      </section>
    </PageShell>
  );
}
